import { LeftOutlined, RightOutlined, TeamOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useEffect, useState } from "react";
import { Sheet } from "react-modal-sheet";
import { IAvailableRoom } from "../types/types";
import { capitalize, capitalizeFirst } from "../utils/functions";
import { toFixedDisplay } from "../utils/number.utils";

/**
 * Bottom sheet that shows a room's photos with back/forth navigation, plus its
 * details. Uses the same `react-modal-sheet` primitive as the scan-to-order
 * product sheet. Reserve is intentionally disabled for now (listings only).
 */
export default function RoomImagesBottomSheet({
  open,
  onClose,
  room,
  nights,
  currency,
}: {
  open: boolean;
  onClose: () => void;
  room?: IAvailableRoom;
  nights: number;
  currency?: string;
}) {
  const [index, setIndex] = useState(0);

  const images = room?.images?.length
    ? room.images
    : ["/assets/images/no-picture.svg"];

  // Reset to the first photo whenever a different room is opened.
  useEffect(() => {
    setIndex(0);
  }, [room?.id]);

  const hasMultiple = images.length > 1;
  const total = nights > 0 ? (room?.pricePerNight ?? 0) * nights : undefined;

  const goPrev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setIndex((i) => (i + 1) % images.length);

  return (
    <Sheet isOpen={open} onClose={onClose}>
      <Sheet.Container>
        <Sheet.Header />
        <Sheet.Content>
          <Sheet.Scroller>
            <div className="p-6 flex flex-col gap-4">
              {/* Gallery */}
              <div className="relative w-full">
                <div className="h-64 w-full bg-gray-100 rounded-xl overflow-hidden">
                  <img
                    src={images[index]}
                    alt={`${room?.name ?? "room"} ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>

                {hasMultiple && (
                  <>
                    <button
                      aria-label="Previous image"
                      onClick={goPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                    >
                      <LeftOutlined />
                    </button>
                    <button
                      aria-label="Next image"
                      onClick={goNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                    >
                      <RightOutlined />
                    </button>

                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.map((_, i) => (
                        <span
                          key={i}
                          onClick={() => setIndex(i)}
                          className={`h-2 w-2 rounded-full cursor-pointer ${
                            i === index ? "bg-white" : "bg-white/50"
                          }`}
                        />
                      ))}
                    </div>

                    <div className="absolute top-2 right-2 rounded-full bg-black/50 text-white text-xs px-2 py-0.5">
                      {index + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Details */}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h1 className="font-bold text-xl leading-tight">
                    {capitalize(room?.name) || "Room"}
                  </h1>
                  {room?.roomTypeName && (
                    <span className="text-sm text-gray-500">
                      {capitalize(room.roomTypeName)}
                    </span>
                  )}
                </div>
                <span className="inline-flex items-center gap-1 text-sm text-gray-600 whitespace-nowrap">
                  <TeamOutlined /> {room?.capacity}
                </span>
              </div>

              {room?.description && (
                <p className="text-sm text-gray-600">
                  {capitalizeFirst(room.description)}
                </p>
              )}

              <div className="flex items-end justify-between border-t border-gray-100 pt-4">
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-gray-900">
                    {currency}
                    {toFixedDisplay(room?.pricePerNight)}
                    <span className="text-xs font-normal text-gray-500">
                      {" "}
                      / night
                    </span>
                  </span>
                  {total !== undefined && (
                    <span className="text-xs text-gray-500">
                      {currency}
                      {toFixedDisplay(total)} for {nights} night
                      {nights > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                <Button type="primary" disabled>
                  Reserve
                </Button>
              </div>

              <button
                className="bg-gray-500 text-white rounded-lg px-4 py-2 mt-2"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </Sheet.Scroller>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onTap={onClose} />
    </Sheet>
  );
}
